import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API from '../../../service/api';
import { ws } from '../../../service/ws';
import { TabsViewer } from '../../../utils/TabsViewer';
import { LocationsGIS } from '../../../components/LocationsGIS';
import { LocationsList } from './LocationsList';
import { LogViewer } from '../../../components/LogViewer';
import { LocationViewerPage } from './locations/[id]';
import { useTranslation } from 'react-i18next';
import { objUpdate } from '../../../service/utils';
import { OperatorNavigationBar } from '../../../components/OperatorNavigationBar';
import { MDBIcon } from 'mdb-react-ui-kit';
import { Alert } from '../../../components/Alert';
import {
    ErrorScreen,
    ErrorScreenProps,
} from '../../../components/errors/ErrorScreen';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { NavLink } from 'react-router-dom';
import { CustomPopover } from '../../../components/popover/CustomPopover';
import ReportSelectorModal from '../../../components/modals/ReportSelectorModal';
import TabsLayout from './TabsLayout';
import { GroupTableViewModal } from './GroupTableViewModal';
import { OpGroupDTO, OpGroupListItemDTO } from '@scm/common-legacy';

export const GroupViewerPage = ({
    userName,
    onLoad,
    groups,
}: {
    userName: string;
    onLoad: (group: OpGroupListItemDTO) => void;
    groups: OpGroupListItemDTO[];
}) => {
    const params = useParams();
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();

    const [fetching, setFetching] = useState(false);
    const [needsToFetch, setNeedsToFetch] = useState(true);
    const [group, setGroup] = useState<OpGroupDTO | null>(null);
    const [error, setError] = useState<ErrorScreenProps | null>(null);
    const [reportSelectorOpened, setReportSelectorOpened] = useState(false);
    const [tableViewOpened, setTableViewOpened] = useState(false);

    const id = useMemo(() => Number(params.id), [params]);
    const groupListItem = useMemo(() => groups.find((g) => g.id == id), [id]);
    const groupName = useMemo(
        () => group?.Name || groups.find((g) => g.id == id)?.Name || '?',
        [group, groups, id],
    );

    useEffect(() => {
        if (groupListItem) onLoad(groupListItem);
    }, [groupListItem]);

    useEffect(() => {
        if (!id) return;
        setNeedsToFetch(true);

        ws.send('group', { groupId: id });

        const unlisten = ws.on(
            'group',
            ({ groupId, updates }: { groupId: number; updates: any }) => {
                if (groupId !== id) return;
                setGroup((prev) => (prev ? objUpdate(prev, updates) : prev));
            },
        );

        return () => {
            unlisten();
            ws.send('group', {});
        };
    }, [id, ws]);

    const fetch = useCallback(async () => {
        setNeedsToFetch(false);
        setFetching(true);
        setError(null);
        setGroup(null);

        try {
            setGroup(await API.GET(`/api/groups/${id}`));
        } catch (err: any) {
            setError({
                title:
                    err.code == 404
                        ? t('errors:not_found')
                        : t('errors:an_issue_occured'),
                description:
                    err.code == 404
                        ? t('errors:group_not_found')
                        : t('errors:group_issue'),
                tryAgain:
                    err.code == 404 ? undefined : () => setNeedsToFetch(true),
            });
        } finally {
            setFetching(false);
        }
    }, [id]);

    useEffect(() => {
        if (needsToFetch && !fetching) fetch();
    }, [needsToFetch, fetching]);

    const ReportPopupSection = useMemo(() => {
        const ReportButton =
            group?.value !== undefined ? (
                <span
                    role={group?.allowReport ? 'button' : 'none'}
                    className="badge badge-success align-items-center"
                    onClick={() =>
                        setReportSelectorOpened(group?.allowReport ?? false)
                    }
                >
                    <MDBIcon
                        icon="hand-holding-dollar"
                        size="lg"
                        fixed
                        color="primary"
                        className="me-1"
                    />
                    {group.value}
                </span>
            ) : null;

        const TableButton =
            group?.locations !== undefined ? (
                <span
                    role="button"
                    className="badge badge-success align-items-center me-2"
                    onClick={() => setTableViewOpened(true)}
                >
                    <MDBIcon
                        icon="table"
                        size="lg"
                        fixed
                        color="primary"
                        className="me-1"
                    />
                    {t('table')}
                </span>
            ) : null;

        return (
            <div className="d-flex flex-row">
                {TableButton}
                {ReportButton}
            </div>
        );
    }, [id, group, innerWidth]);

    const GroupSelector = useMemo(
        () => (
            <CustomPopover
                placement={innerWidth < 1000 ? 'top-end' : 'bottom'}
                trigger={
                    <span
                        role="button"
                        className="d-flex flex-row align-items-center"
                    >
                        <MDBIcon
                            icon="layer-group"
                            color="primary"
                            size="lg"
                            className="me-1"
                        />
                        <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                            {group?.defaultGroup && (
                                <span>
                                    <MDBIcon
                                        icon="bookmark"
                                        color="warning"
                                        className="me-1"
                                    />
                                </span>
                            )}
                            {groupName}
                        </span>
                        <span>
                            <MDBIcon icon="bars" fixed size="sm" />
                        </span>
                    </span>
                }
            >
                {(close) => (
                    <div
                        className="d-flex flex-column overflow-auto"
                        style={{ maxHeight: 500 }}
                    >
                        {(!groups || groups.length == 0) && (
                            <div className="custom-select-item">
                                {t('no_groups')}
                            </div>
                        )}
                        {groups &&
                            groups.map((g) => (
                                <NavLink
                                    key={g.Name}
                                    to={`/group/${g.id}`}
                                    onClick={close}
                                >
                                    <div
                                        className={
                                            'custom-select-item text-dark' +
                                            (g.defaultGroup
                                                ? ' list-group-item-warning fst-italic warning-row'
                                                : '')
                                        }
                                    >
                                        <MDBIcon
                                            icon="layer-group"
                                            color="primary"
                                            className="me-1"
                                        />
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {g.defaultGroup && (
                                                <span>
                                                    <MDBIcon
                                                        icon="bookmark"
                                                        color="warning"
                                                        className="me-1"
                                                    />
                                                </span>
                                            )}
                                            {g.Name}
                                        </span>
                                    </div>
                                </NavLink>
                            ))}
                    </div>
                )}
            </CustomPopover>
        ),
        [groupName, group, params, groups, innerWidth],
    );

    return (
        <Routes>
            <Route
                path=""
                element={
                    <div
                        className="d-flex flex-column flex-fill justify-content-center align-items-center"
                        style={{ overflowX: 'hidden' }}
                    >
                        <ReportSelectorModal
                            open={reportSelectorOpened}
                            onClose={() => setReportSelectorOpened(false)}
                            groupId={group?.id ?? -1}
                        />
                        <GroupTableViewModal
                            open={tableViewOpened}
                            onClose={() => setTableViewOpened(false)}
                            groupId={id}
                            groupName={groupName}
                        />
                        <OperatorNavigationBar
                            right={ReportPopupSection}
                            links={[]}
                            centerContent={GroupSelector}
                        />
                        {fetching && <LoadingSpinner />}
                        {error && (
                            <div className="flex-fill my-5">
                                <ErrorScreen {...error} />
                            </div>
                        )}

                        {group && <TabsLayout group={group} />}
                    </div>
                }
            />
            {group && (
                <>
                    <Route
                        path="locations/:id/*"
                        element={
                            <LocationViewerPage
                                groupId={id}
                                groupName={group.Name}
                                locations={group.locations || []}
                            />
                        }
                    />
                    <Route path="*" element={<Navigate to="" />} />
                </>
            )}
        </Routes>
    );
};
